import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { ArrowLeft, Shield, FileText, Scale } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- CONTENT DATABASE (Expanded Version) ---
const DOCS = {
  privacy: {
    title: "Privacy Policy",
    icon: <Shield size={24} color="#2563eb" />,
    updated: "January 24, 2026",
    content: (
      <>
        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>1. Introduction</h3>
        <p>SiteACT ("we", "our") is committed to protecting the privacy of our users. We act as a <strong>Data Processor</strong> for the construction companies ("Data Controllers") that use our platform.</p>
        
        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>2. Data We Collect</h3>
        <p>To provide our services, we collect and process:</p>
        <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: '10px 0' }}>
            <li><strong>Identity Data:</strong> Names, photographs (for worker profiles), and contact details.</li>
            <li><strong>Compliance Data:</strong> Certification documents, licenses, and training records.</li>
            <li><strong>Usage Data:</strong> Geo-location tags (only when signing in/out or signing forms), IP addresses, and device logs.</li>
        </ul>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>3. How We Store Data</h3>
        <p>All data is encrypted at rest using AES-256 and stored on Microsoft Azure servers located in the <strong>UK South (London)</strong> region. We retain data for the duration of your subscription plus 90 days, or as required by law.</p>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>4. Third-Party Sub-processors</h3>
        <p>We share minimal necessary data with the following infrastructure providers:</p>
        <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: '10px 0' }}>
            <li><strong>Microsoft Azure:</strong> Cloud hosting and database storage.</li>
            <li><strong>Stripe:</strong> Payment processing (we do not store credit card details).</li>
            <li><strong>SendGrid:</strong> Transactional email delivery.</li>
        </ul>
      </>
    )
  },
  terms: {
    title: "Terms of Service",
    icon: <FileText size={24} color="#2563eb" />,
    updated: "January 24, 2026",
    content: (
      <>
        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>1. The Service</h3>
        <p>SiteACT is a record-keeping and compliance management tool. <strong>We are not a safety advisory firm.</strong> You acknowledge that SiteACT does not provide professional health and safety advice and that you remain solely responsible for physical safety on your sites.</p>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>2. Limitation of Liability (Important)</h3>
        <p>To the maximum extent permitted by law, SiteACT's total liability for any claims arising from these terms (including negligence) shall be limited to the <strong>total amount paid by you in the 12 months preceding the claim</strong>.</p>
        <p>We are not liable for indirect or consequential losses, including project delays, loss of profit, or third-party contract penalties.</p>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>3. Payment & Cancellation</h3>
        <p>Subscriptions are billed in advance. You may cancel at any time via the dashboard. Refunds are not provided for partial months. If payment fails, we reserve the right to suspend access to the platform after a 7-day grace period.</p>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>4. Fair Use Policy</h3>
        <p>"Unlimited Users" and "Unlimited Projects" are subject to a fair use limit of 500GB storage per active site license. Usage beyond this may incur additional storage fees.</p>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>5. Governing Law</h3>
        <p>These terms are governed by the laws of <strong>England and Wales</strong>. Any disputes shall be subject to the exclusive jurisdiction of the courts of London.</p>
      </>
    )
  },
  gdpr: {
    title: "GDPR Statement",
    icon: <Scale size={24} color="#2563eb" />,
    updated: "January 24, 2026",
    content: (
      <>
        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>1. Data Subject Rights</h3>
        <p>As a platform user, you have the right to:</p>
        <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: '10px 0' }}>
            <li><strong>Access:</strong> Request a copy of all data we hold about you.</li>
            <li><strong>Rectification:</strong> Correct inaccurate personal data.</li>
            <li><strong>Erasure:</strong> Request deletion of your data ("Right to be Forgotten"), subject to legal retention requirements for construction records.</li>
        </ul>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>2. Data Portability</h3>
        <p>We believe your data belongs to you. Administrators can export full project records (including PDFs, images, and logs) in standard formats (CSV/ZIP) at any time without assistance from support.</p>

        <h3 style={{fontSize: '1.2rem', marginTop: '20px', color: '#1e293b'}}>3. Data Processing Agreement (DPA)</h3>
        <p>For Enterprise clients requiring a signed DPA, please contact legal@siteact.com. Our standard DPA incorporates the EU Standard Contractual Clauses (SCCs).</p>
      </>
    )
  }
};

const LegalDoc = ({ type }) => {
  const navigate = useNavigate();
  const doc = DOCS[type];
  
  // --- ADDED MOBILE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Theme definition for Navbar consistency
  const theme = {
    primary: '#3ba9e1', 
    secondary: '#0f172a', 
    navBg: '#132238', 
    navText: '#ffffff',
    bgLight: '#f8fafc', 
    bgWhite: '#ffffff'
  };

  if (!doc) return <div>Document not found</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar theme={theme} />
      
      <div style={{ 
        flex: 1, 
        padding: isMobile ? '100px 20px 40px' : '120px 20px 80px', 
        maxWidth: '800px', 
        margin: '0 auto', 
        width: '100%',
        boxSizing: 'border-box' // <-- Fixes horizontal scrolling
      }}>
        <button 
            onClick={() => navigate('/')}
            style={{ 
              background: 'none', border: 'none', cursor: 'pointer', 
              display: 'flex', alignItems: 'center', gap: '8px', 
              color: '#64748b', marginBottom: '30px', fontWeight: '500'
            }}
          >
            <ArrowLeft size={16} /> Back to Home
        </button>

        <div style={{ 
          backgroundColor: 'white', 
          padding: isMobile ? '25px' : '50px', // <-- Reduces inner padding on phones
          borderRadius: '16px', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          boxSizing: 'border-box' // <-- Keeps inner content contained
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px', 
            marginBottom: '20px', 
            paddingBottom: '20px', 
            borderBottom: '1px solid #e2e8f0',
            flexWrap: isMobile ? 'wrap' : 'nowrap' // <-- Allows header to stack on very small screens
          }}>
            <div style={{ padding: '10px', background: '#eff6ff', borderRadius: '8px' }}>
              {doc.icon}
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: isMobile ? '1.6rem' : '2rem', color: '#0f172a' }}>{doc.title}</h1>
              <p style={{ margin: '5px 0 0', color: '#94a3b8', fontSize: '0.9rem' }}>Last Updated: {doc.updated}</p>
            </div>
          </div>

          <div style={{ lineHeight: '1.8', color: '#334155', fontSize: isMobile ? '1rem' : '1.05rem' }}>
            {doc.content}
          </div>
        </div>
      </div>

      <Footer theme={theme} />
    </div>
  );
};

export default LegalDoc;